import { DEPLOYMENT_ENVIRONMENT, DeploymentEnvironment } from 'env/environments';
import { getHomeLoansDashboardRuntimeConfig } from 'src/services/config/getHomeLoansDashboardRuntimeConfig';
import { MACRuntimeConfig } from 'env/types';
import { UTMParameters } from 'src/analytics/types';
import { getIntegrationZillowOriginForDeploymentEnvironment } from './getters/getIntegrationZillowOriginForDeploymentEnvironment';

export type ZillowHomeLoansURLConfig = {
    useLocal?: boolean;
    utmParameters?: UTMParameters;
    queryParameters?: Record<string, string>;
    loanID?: string;
};

export type GetLoanIDFromURLResult = {
    loanID: string | null;
    isQueryParameter: boolean;
};

/**
 * Co-effects: NextJS deployment environment
 *
 * Contains the necessary logic for working with any ZHL or Zillow route across all deployed environments, handling
 * the conversion of deprecated query parameters to URL parameters, and the automatic encoding of requested query parameters
 *
 */
export class ZillowHomeLoansURL {
    private requestedURL: string;
    private urlConfig: ZillowHomeLoansURLConfig;
    private config: MACRuntimeConfig;
    private static readonly ENDS_WITH_DASHBOARD_AND_SLASH = new RegExp(/\/dashboard\/$/gi);
    // One for the whole path, one for the group
    private static readonly EXPECTED_MATCHES_FOR_LOAN_ID_IN_URL = 2;

    private static readonly LOAN_ID_QUERY_PARAMETER = 'loanID';

    private static readonly LOAN_ID_AS_URL_PARAMETER_REGEX = new RegExp(/\/mortgage\/dashboard\/([a-zA-Z0-9\-]+)\/?/gi);

    private static readonly RELATIVE_PATH_DEPLOYMENT_ENVIRONMENTS: Partial<DeploymentEnvironment>[] = [
        DEPLOYMENT_ENVIRONMENT.LOCAL,
        // To support both dev environment and MRs
        DEPLOYMENT_ENVIRONMENT.DEVELOPMENT,
    ];
    private static readonly APPLICATION_DEPLOYMENT_PREFIX = '/homeloans';

    private static readonly ENV_TO_TOP_LEVEL_DOMAIN: Record<DeploymentEnvironment, string> = {
        [DEPLOYMENT_ENVIRONMENT.DEVELOPMENT]: '.net',
        [DEPLOYMENT_ENVIRONMENT.LOCAL]: `.net`,
        [DEPLOYMENT_ENVIRONMENT.STAGING]: '.net',
        [DEPLOYMENT_ENVIRONMENT.PRODUCTION]: '.com',
    };
    public constructor(requestedURL: string, urlConfig: ZillowHomeLoansURLConfig = {}) {
        this.requestedURL = requestedURL;
        this.urlConfig = urlConfig;
        this.config = getHomeLoansDashboardRuntimeConfig();
    }

    /**
     * Allows for resolving a URL with intentional targeting of a specific deployment environment rather than the current one
     *
     * Note: This method does enforces the `/homeloans` prefix
     */
    public static getZillowHomeLoansURLForDeploymentEnvironment(
        requestedURL: string,
        { urlConfig = {}, config }: { urlConfig?: ZillowHomeLoansURLConfig; config: MACRuntimeConfig }
    ) {
        const relativePath = this.resolveRelativePathForDeploymentEnvironment(requestedURL, config);

        const sanitizedRelativePath = this.getPathWithSanitizedQueryParameters(relativePath, urlConfig);
        if (this.shouldUseRelativePathForDeploymentEnvironment(config, urlConfig)) {
            return sanitizedRelativePath;
        }
        const zillowDomain = getIntegrationZillowOriginForDeploymentEnvironment();
        return `${zillowDomain}${sanitizedRelativePath}`;
    }

    /**
     * Gets the loan ID from a URL, supporting both query parameter and URL parameter loan ID
     */
    public static getLoanIDFromURL(path: string): GetLoanIDFromURLResult {
        const queryParams = this.getQueryParametersFromURL(path);
        const [matches] = [...path.matchAll(this.LOAN_ID_AS_URL_PARAMETER_REGEX)];
        const urlParamLoanID = (matches?.length ?? 0) < this.EXPECTED_MATCHES_FOR_LOAN_ID_IN_URL ? null : matches[1];
        const queryParamLoanID = queryParams.get(this.LOAN_ID_QUERY_PARAMETER);
        return {
            loanID: queryParamLoanID ?? urlParamLoanID,
            isQueryParameter: !!queryParamLoanID,
        };
    }

    /**
     * Retrieves the query parameters present in the passed in path, and removes any disallowed query parameters
     */
    public static getSanitizedQueryParameters(path: string, urlConfig: ZillowHomeLoansURLConfig = {}) {
        const { utmParameters = {}, queryParameters = {} } = urlConfig;
        const existingQueryParams = this.getQueryParametersFromURL(path);
        Object.entries({
            ...queryParameters,
            ...utmParameters,
        }).forEach(([key, value]) => {
            existingQueryParams.set(key, value);
        });
        existingQueryParams.delete(this.LOAN_ID_QUERY_PARAMETER);
        return existingQueryParams;
    }

    /**
     * Returns an equivalent path with appended query parameters, sans any disallowed query parameters.
     *
     * Note: This method does not enforce the `/homeloans` prefix
     */
    public static getPathWithSanitizedQueryParameters(path: string, urlConfig: ZillowHomeLoansURLConfig = {}): string {
        const injectedPath = this.injectLoanID(path, urlConfig);
        const queryParams = this.getSanitizedQueryParameters(injectedPath, urlConfig);
        const [pathWithoutQueryParams] = injectedPath.split('?');
        // I would use URLSearchParams.size here but for some reason it is undefined
        if (!Array.from(queryParams.entries()).length) {
            return pathWithoutQueryParams;
        }
        return `${pathWithoutQueryParams}?${queryParams.toString()}`;
    }

    /**
     * Returns the requested URL, correctly reformatted for the deployment environment, with the appropriate query parameters
     * and URL parameters configured, and the /homeloans prefix enforced
     */
    public get resolvedURL() {
        return ZillowHomeLoansURL.getZillowHomeLoansURLForDeploymentEnvironment(this.requestedURL, {
            urlConfig: this.urlConfig,
            config: this.config,
        });
    }

    /**
     * Returns the requested URL unchanged
     */
    public get rawURL() {
        return this.requestedURL;
    }

    /**
     * Gets the full set of query parameters from a URL without doing any sanitization of disallowed parameters
     */
    private static getQueryParametersFromURL(path: string) {
        if (!path.includes('?')) {
            return new URLSearchParams();
        }
        const [, params] = path.split('?');
        return new URLSearchParams(params);
    }

    /**
     * Injects the requested loan ID as a URL Parameter, otherwise converts a detected loan ID query parameter to a URL parameter
     */
    private static injectLoanID(path: string, urlConfig: ZillowHomeLoansURLConfig) {
        if (urlConfig.loanID) {
            return this.setLoanIDURLParameter(path, urlConfig.loanID);
        }

        const { loanID, isQueryParameter } = this.getLoanIDFromURL(path);

        return isQueryParameter && loanID ? this.setLoanIDURLParameter(path, loanID) : path;
    }

    /**
     * Determines whether a path should be converted to an absolute path based on both deployment environment and URL configuration
     */
    private static shouldUseRelativePathForDeploymentEnvironment(
        config: MACRuntimeConfig,
        urlConfig: ZillowHomeLoansURLConfig
    ) {
        const { ENV } = config.publicRuntimeConfig;
        return this.RELATIVE_PATH_DEPLOYMENT_ENVIRONMENTS.includes(ENV) || urlConfig.useLocal;
    }

    private static resolveRelativePathForDeploymentEnvironment(requestedURL: string, config: MACRuntimeConfig) {
        const { ENV } = config.publicRuntimeConfig;
        const topLevelDomain = this.ENV_TO_TOP_LEVEL_DOMAIN[ENV];
        const isURLFullyResolved = requestedURL.includes(topLevelDomain);
        const resolvedURL = isURLFullyResolved ? requestedURL.split(topLevelDomain)[1] : requestedURL;
        return this.getZillowPath(resolvedURL);
    }

    /**
     * Converts a relative dashboard path into a relative dashboard path with the Zillow domain prefix.
     * Doesn't duplicate the zillow prefix if the path already contains it.
     */
    private static getZillowPath(localPath: string) {
        if (localPath.startsWith(this.APPLICATION_DEPLOYMENT_PREFIX)) {
            return localPath;
        }
        return `${this.APPLICATION_DEPLOYMENT_PREFIX}${localPath}`;
    }

    private static setLoanIDURLParameter(path: string, loanID: string) {
        const [actualPath, queryParamsIfTheyExist] = path.split('?');
        if (actualPath.match(this.ENDS_WITH_DASHBOARD_AND_SLASH)) {
            return `${actualPath}${loanID}/${queryParamsIfTheyExist ? `?${queryParamsIfTheyExist}` : ''}`;
        }
        return `${actualPath}/${loanID}/${queryParamsIfTheyExist ? `?${queryParamsIfTheyExist}` : ''}`;
    }
}
